"use client";

import { Close24xLarge } from "@/components/icons";
import { ZoomIn } from "@/components/icons/Zoom";
import { Carousel, CarouselItem } from "@/components/ui/Carousel";
import { ModalClose, ModalRoot, ModalViewport } from "@/components/ui/modal";
import { Text } from "@/components/ui/Text";
import { useDevice } from "@/features/Device";
import type { GetProductPricePromise } from "@/features/products/dal";
import type { FormattedMedia } from "@/lib/centra/formatters/media";
import { sizes } from "@/lib/utils";
import { clsx } from "@frend-digital/ui";
import { atom, useAtom } from "jotai";
import Image from "next/image";
import { memo, Suspense, useState } from "react";
import styles from "./index.module.css";
import { UnsuspendedSaleTag } from "./SaleTag";
import { ZoomableCarouselImage } from "./ZoomableCarouselImage";

const galleryModalOpenAtom = atom<boolean>(false);

export interface ProductMediaProps {
	media: FormattedMedia;
}

export const ProductMedia = memo(
	({
		media,
		pricePromise,
	}: {
		media: FormattedMedia[];
		pricePromise?: GetProductPricePromise;
		className?: string;
	}) => {
		const [index, setIndex] = useState(0);
		const { mobile } = useDevice();

		const isSingle = media.length < 2;

		const [_, setIsOpen] = useAtom(galleryModalOpenAtom);

		return (
			<section className={styles.media}>
				<GalleryModal>
					<Carousel
						onIndexChange={(index) => setIndex(index)}
						itemsPerSlide={1}
						pagination={media.length > 1}
						className={styles.modalCarousel}>
						{media.map((m, index) => {
							return (
								<CarouselItem key={index} asChild>
									<div className={styles.modalImage}>
										{mobile ? (
											<Image src={m.href} alt={m.alt} fill />
										) : (
											<ZoomableCarouselImage src={m.href} alt={m.alt} />
										)}
									</div>
								</CarouselItem>
							);
						})}
					</Carousel>
					<div className={clsx(styles.scrollbar, styles.always)}>
						<span
							className={styles.track}
							style={{
								width: `${(1 / media.length) * 100}%`,
								transform: `translateX(${100 * index + 1}%)`,
							}}
						/>
					</div>
				</GalleryModal>

				<Carousel
					onIndexChange={(index) => setIndex(index)}
					options={{
						align: mobile ? "center" : "start",
						loop: mobile,
						dragFree: false,
					}}
					pagination={media.length > 2}
					classNames={{
						root: clsx(styles.carouselRoot, isSingle && styles.isSingle),
					}}
					itemsPerSlide={"ignore"}>
					{media.map((m, index) => {
						return (
							<CarouselItem
								className={styles.carouselSlide}
								key={index}
								onClick={() => setIsOpen(true)}>
								{index === 0 && pricePromise && (
									<Suspense>
										<UnsuspendedSaleTag pricePromise={pricePromise} />
										<button role="button" className={styles.zoomIcon}>
											<ZoomIn />
										</button>
									</Suspense>
								)}

								<div className={styles.imageContainer}>
									<Image
										priority={index <= 1}
										sizes={sizes(100, 50)}
										src={m.href}
										alt={m.alt}
										fill
									/>
									{m.img_attributes_label && (
										<Text className={styles.label} variant="utility4">
											{m.img_attributes_label}
										</Text>
									)}
								</div>
							</CarouselItem>
						);
					})}
				</Carousel>
				<div className={styles.scrollbar}>
					<span
						className={styles.track}
						style={{
							width: `${(1 / media.length) * 100}%`,
							transform: `translateX(${100 * index + 1}%)`,
						}}
					/>
				</div>
			</section>
		);
	},
);

export const GalleryModal = ({ children }: { children: React.ReactNode }) => {
	const [isOpen, setIsOpen] = useAtom(galleryModalOpenAtom);

	return (
		<ModalRoot open={isOpen} onOpenChange={setIsOpen}>
			<ModalViewport className={styles.modal}>
				<ModalClose className={styles.modalClose}>
					<Close24xLarge />
				</ModalClose>
				{children}
			</ModalViewport>
		</ModalRoot>
	);
};

ProductMedia.displayName = "ProductMedia";
